import React, { useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Svg, Typography, useThemeValue } from '@altermaind/web/ui';
import { convertSizeToNumber } from '@/_shared/helpers/convertSizeToNumber';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { useNotification } from '@/_shared/hooks/useNotification';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getDelegatesCall, deleteDelegateCall } from '@/organization/state';

export const AddedDelegatesList = () => {
  const t = useTranslations('configuration.hr.delegate.confirm.currentDelegates');
  const [loadingListAfterDelete, setLoadingListAfterDelete] = useState(false);
  const sharedT = useTranslations();
  const { genericResponsiveValue } = useResponsiveStyle();
  const { isMobile } = useBreakingPoints();
  const { notify } = useNotification();
  const gapXS = convertSizeToNumber(`${useThemeValue('sizes', 'xs')}`);
  const spaceM = convertSizeToNumber(`${useThemeValue('sizes', 'm')}`) || 24;
  const { execute: executeGetDelegates, hrDelegates } = getDelegatesCall.hook();
  const { execute: executeDeleteDelegate, loading: loadingDeleteDelegate } =
    deleteDelegateCall.hook();

  const handleDelete = (userId: string) => {
    executeDeleteDelegate({
      userId,
      successSaga: () => {
        setLoadingListAfterDelete(true);
        executeGetDelegates({
          start: 0,
          length: 1000,
          successSaga: () => {
            // Refresh the HR delegates list after deletion
            notify(t('deleted'), 'success');
            setLoadingListAfterDelete(false);
          },
          errorSaga: () => {
            notify(sharedT('errors.genericApiError'), 'error');
            setLoadingListAfterDelete(false);
          },
        });
      },
      errorSaga: () => {
        notify(sharedT('errors.genericApiError'), 'error');
      },
    });
  };

  return (
    <Box
      display='flex'
      flex={genericResponsiveValue({
        breakPoints: { desktop: 4 },
        defaultValue: 10,
      })}
      style={{
        gap: gapXS,
        // On desktop the delegates column scrolls on its own so the form and the
        // wizard navigation on the left stay fixed; on mobile it falls back to
        // the natural page scroll.
        maxHeight: genericResponsiveValue({
          breakPoints: {
            desktop: `calc(100vh - ${spaceM}px)`,
            desktopLarge: `calc(100vh - ${spaceM}px)`,
          },
          defaultValue: 'none',
        }),
        overflowY: genericResponsiveValue<'auto' | 'visible'>({
          breakPoints: { desktop: 'auto', desktopLarge: 'auto' },
          defaultValue: 'visible',
        }),
      }}
      flexDirection='row'
      flexWrap='wrap'
      width='100%'
    >
      {hrDelegates.map(hr => (
        <Box flex={1} key={hr.email}>
          <Card width='100%'>
            <Typography variant='h2' mb='xxs'>
              {t('title')}
            </Typography>
            <Box
              borderColor='grey/dark/background'
              border='2px solid'
              display='flex'
              flexDirection={genericResponsiveValue({
                breakPoints: { desktop: 'row' },
                defaultValue: 'column',
              })}
              alignItems={genericResponsiveValue({
                breakPoints: { desktop: 'center' },
                defaultValue: 'flex-start',
              })}
              borderRadius={16}
              p='xs'
              mb='s'
              mt='xs'
            >
              <Box
                flex='1'
                mb={genericResponsiveValue({
                  breakPoints: { desktop: 'none' },
                  defaultValue: 'xs',
                })}
              >
                <Box>
                  <Typography variant='p3/label' color='grey/light'>
                    {t('firstNameAndLastName')}
                  </Typography>
                  <Typography variant='p2/bold'>{`${hr.firstName} ${hr.lastName}`}</Typography>
                </Box>
              </Box>
              <Box
                flex='1'
                mb={genericResponsiveValue({
                  breakPoints: { desktop: 'none' },
                  defaultValue: 'xs',
                })}
              >
                <Box>
                  <Typography variant='p3/label' color='grey/light'>
                    {t('email')}
                  </Typography>
                  <Typography variant='p2/bold'>{hr.email}</Typography>
                </Box>
              </Box>
              <Button
                spinnerColor='white'
                onClick={() => handleDelete(hr.id)}
                disabled={loadingDeleteDelegate || loadingListAfterDelete}
                loading={loadingDeleteDelegate || loadingListAfterDelete}
                width='auto'
                flex={0}
                minWidth={'auto' as never}
                padding={0}
                bg='transparent'
                style={{ margin: isMobile ? '0 0 0 auto' : undefined }}
              >
                <Svg name='trash' size='s' fill='primary/text' />
              </Button>
            </Box>
          </Card>
        </Box>
      ))}
    </Box>
  );
};
